import React from 'react'
import Modal from 'LuxKit/Modal/Modal'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import Hr from 'components/Common/Hr'
import HelpPane from 'components/Common/HelpPane'

const ModalContent = styled.div`
  padding-top: ${rem(16)};
`

const CancellationTermsWrapper = styled.div`
  display: grid;
  grid-column-gap: ${rem(18)};
  margin-top: ${rem(40)};

  ${mediaQueryUp.tablet} {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
  }
`

const TermsGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(16)};
  grid-row-gap: ${rem(18)};
  grid-template-columns: 1fr 2fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

interface Props {
  checkInDate?: string | moment.Moment
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment | undefined
  cancellationAfterChangeOfMindPeriod: boolean
  days: number
}

function OfferPropertyFlexibleCancellationPolicyModal(props: Props) {
  const {
    checkInDate,
    startBasedOnToday,
    cancellationAfterChangeOfMindPeriod,
    startBasedOnCheckIn,
    days,
  } = props

  const intlDateFormatter = useIntlDateFormatter()

  return <Modal
    title="Cancellation policy"
    size="L"
  >
    <ModalContent>
      <BodyText variant="medium" as="p">
        Things don't always work out, so enjoy peace of mind knowing that the following cancellation options are available to you.{' '}
      </BodyText>
      <CancellationTermsWrapper>
        <TermsGrid>
          {!checkInDate && <BodyText variant="medium" weight="bold" as="span">
            Up to 7 days after purchase
          </BodyText>}
          {checkInDate && <BodyText variant="medium" weight="bold" as="span">
            Cancel until {intlDateFormatter(startBasedOnToday, INTL_DMY_CASUAL_SHORT_FORMAT)}
          </BodyText>}
          <BodyText variant="medium" as="span">
            100% refund of accommodation purchase price to your original payment method or credit {!checkInDate && <>(provided cancellation is made at least 14 days prior to check-in)</>}
          </BodyText>
        </TermsGrid>
        <Hr size="md" />
        {cancellationAfterChangeOfMindPeriod && startBasedOnCheckIn && <>
          <TermsGrid>
            <BodyText variant="medium" weight="bold" as="span">
              Cancel until {intlDateFormatter(startBasedOnCheckIn, INTL_DMY_CASUAL_SHORT_FORMAT)}
            </BodyText>
            <BodyText variant="medium" as="span">
              100% refund of accommodation purchase price to credit
            </BodyText>
          </TermsGrid>
          <Hr size="md" />
        </>}
        {!cancellationAfterChangeOfMindPeriod && !checkInDate && <>
          <TermsGrid>
            <BodyText variant="medium" weight="bold" as="span">
              Up to {days + 1} days prior to check-in
            </BodyText>
            <BodyText variant="medium" as="span">
              100% refund of accommodation purchase price to credit
            </BodyText>
          </TermsGrid>
          <Hr size="md" />
        </>}
        <TermsGrid>
          {!checkInDate && <BodyText variant="medium" weight="bold" as="span">
            From {days} days until check-in
          </BodyText>}
          {checkInDate && startBasedOnCheckIn && <BodyText variant="medium" weight="bold" as="span">
            Cancel after {intlDateFormatter(startBasedOnCheckIn, INTL_DMY_CASUAL_SHORT_FORMAT)}
          </BodyText>}
          <BodyText variant="medium" as="span">
            Non-refundable
          </BodyText>
        </TermsGrid>
      </CancellationTermsWrapper>
      <HelpPane />
    </ModalContent>
  </Modal>
}

export default OfferPropertyFlexibleCancellationPolicyModal
